import { useEffect, useState } from 'react';

/**
 * True once the page has seen its FIRST direct evidence of a human — a pointer
 * movement, touch, key press, wheel or scroll. False during SSR, hydration, and
 * for renderers that dispatch no trusted input events.
 *
 * R-03: the Dynamic Maps SKU bills per map instantiation, and search-engine
 * crawlers execute JavaScript — pre-gate, every crawler render of the 1,434
 * map-bearing routes could start a billable map nobody sees. Crawler renderers
 * (Googlebot WRS included) do not produce these input events, so gating map
 * creation on this signal stops bot-triggered map spend entirely, while a real
 * visitor produces a qualifying event within moments of arrival and never
 * notices the deferral.
 *
 * Deliberately NOT persisted (no sessionStorage): the cost is per page-load, so
 * the signal is re-established per mount — one listener set, removed on first fire.
 */
const HUMAN_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'] as const;

export function useHumanSignal(): boolean {
    const [seen, setSeen] = useState(false);

    useEffect(() => {
        if (seen) return;
        let fired = false;
        const onSignal = (ev: Event) => {
            // Only TRUSTED events count — real user agents produce them; scripts
            // (including a crawler's own page scripts, and test runners) cannot.
            // This is what keeps synthetic dispatches from counterfeiting a human.
            if (!ev.isTrusted) return;
            if (fired) return;
            fired = true;
            for (const e of HUMAN_EVENTS) window.removeEventListener(e, onSignal);
            setSeen(true);
        };
        for (const e of HUMAN_EVENTS) window.addEventListener(e, onSignal, { passive: true });
        return () => {
            for (const e of HUMAN_EVENTS) window.removeEventListener(e, onSignal);
        };
    }, [seen]);

    return seen;
}
